import type { ChangeEvent } from 'react';
import React from 'react';

import { Alert, Divider, FormControlLabel, Typography } from '@mui/material';
import { Lock } from 'lucide-react';
import type { ShareDocumentDialogAction, ShareDocumentDialogState } from 'src/components';
import {
  AlineCheckbox,
  AlineTextarea,
  DocumentForwardingToggle,
  PermissionList,
  ShareDocumentDialogActionType,
} from 'src/components';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  accessText: {
    margin: theme.spacing(2, 0),
  },
  checkBox: {
    margin: theme.spacing(1, 0),
  },
  internalNotice: {
    fontSize: 14,
    marginTop: theme.spacing(2),
  },
  textArea: {
    flexGrow: 1,
  },
}));

export interface ShareDocumentDialogDocument extends Pick<
  Apollo.Document,
  'id' | 'forwardingPermissionRole' | 'requesterRole' | '__typename'
> {
  owner: Pick<Apollo.User, '__typename' | 'id'>;
}

interface ShareDialogContentProps {
  autocompleteEmpty: boolean;
  dialogDispatch: React.Dispatch<ShareDocumentDialogAction>;
  dialogState: ShareDocumentDialogState;
  document: ShareDocumentDialogDocument;
  disabled?: boolean;
}

export function ShareDocumentDialogDetails({
  autocompleteEmpty,
  dialogDispatch,
  dialogState,
  disabled,
  document,
}: ShareDialogContentProps): JSX.Element {
  const { classes } = useStyles();
  const { message, notifyRecipients } = dialogState;

  function handleChangeTextArea(event: ChangeEvent<HTMLTextAreaElement>): void {
    dialogDispatch({
      payload: { message: event.target.value },
      type: ShareDocumentDialogActionType.SET_DIALOG_STATE,
    });
  }

  if (autocompleteEmpty)
    return (
      <>
        <InternalDocumentNotice documentId={document.id} />
        <Typography className={classes.accessText} variant="subtitle1">
          General access
        </Typography>
        <Divider variant="fullWidth" />
        <PermissionList disabled={disabled} documentId={document.id} />
        <Divider variant="fullWidth" />
        <Typography className={classes.accessText} variant="subtitle1">
          Document forwarding
        </Typography>
        <DocumentForwardingToggle document={document} />
      </>
    );

  return (
    <>
      <InternalDocumentNotice documentId={document.id} />
      <FormControlLabel
        className={classes.checkBox}
        control={
          <AlineCheckbox
            checked={notifyRecipients}
            color="primary"
            onChange={(): void => {
              dialogDispatch({
                payload: { notifyRecipients: !notifyRecipients },
                type: ShareDocumentDialogActionType.SET_DIALOG_STATE,
              });
            }}
          />
        }
        label="Notify recipients"
      />
      {notifyRecipients && (
        <AlineTextarea
          className={classes.textArea}
          maxRows={10}
          minRows={10}
          value={message}
          onChange={handleChangeTextArea}
        />
      )}
    </>
  );
}

// A direct share is the deliberate exception to a document being Internal on its workflow.
function InternalDocumentNotice({ documentId }: { documentId: UUID }): JSX.Element | null {
  const { classes } = useStyles();
  const { data } = Apollo.useGetDocumentIsInternalOnWorkflowQuery({ variables: { documentId } });
  if (!data?.document.isInternalOnWorkflow) return null;

  return (
    <Alert className={classes.internalNotice} icon={<Lock size={16} />} severity="info">
      This document is internal on its workflow. Anyone you share it with here can open it.
    </Alert>
  );
}
